import React, { useEffect, useMemo } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import Dropdown, { IDropdownItem } from '../../../ui/Dropdown/Dropdown'
import { observer } from 'mobx-react'
import rootStore from '../../../../store/RootStore'
import Message from '../Message/Message'
import { getDrilldownModes } from '../../../../store/WizardStore'
import { IReportBlueprintChart } from '../../../../services/decoders/blueprintDecoder'

const DrilldownSelector = observer(() => {
  const {
    wizardStore: {
      selectedNodeId,
      loadChartSeries,
      chartSeries,
      error,
      currentChart,
      setError,
      availableDrilldowns,
      setDrilldownMode,
      drilldownModeSelected,
      updateDrilldown,
    },
  } = rootStore

  useEffect(() => {
    loadChartSeries()

    /**
     * Set the drilldown mode to the one defined in the chart
     */
    setDrilldownMode(
      getDrilldownModes().find(
        mode => mode.type === (currentChart.drillDownInfo?.type ?? 0),
      ),
    )
  }, [loadChartSeries, selectedNodeId, currentChart, setDrilldownMode])

  return (
    <Box>
      {error && (
        <Message
          type="info"
          message={error}
          onClose={() => setError(null)}
        />
      )}
      <Text textStyle="h3">Chart: {currentChart?.name}.</Text>
      <Box m="16px 0">
        Drilldowns can be defined for (i) anywhere in the chart (default); (II)
        X axis; (III) Y axis, in your dataset.
      </Box>
      {!selectedNodeId && (
        <Box
          mt="16px"
          p="12px"
          bgColor="brand.lightGray"
          fontSize="14px">
          Select a chart to see drilldown options.
        </Box>
      )}

      {selectedNodeId && (
        <Box>
          <Text fontWeight="bold">Link Mode:</Text>
          <Dropdown
            key={drilldownModeSelected.id}
            items={getDrilldownModes() as unknown as IDropdownItem[]}
            onChange={drilldownMode => setDrilldownMode(drilldownMode)}
            buttonProps={{
              fontSize: '14px',
              textAlign: 'left',
              w: '100%',
              borderBottom: '1px solid',
              borderBottomColor: 'gray.200',
              margin: 0,
              borderRadius: 0,
            }}
            shouldUsePortal={false}
            selected={drilldownModeSelected as unknown as IDropdownItem}
          />

          <Linkages
            type={drilldownModeSelected.description}
            linkTargets={
              chartSeries[selectedNodeId]?.[drilldownModeSelected.value]
            }
            drilldowns={availableDrilldowns as unknown as IDropdownItem[]}
            updateDrilldown={updateDrilldown}
            currentChart={currentChart}
          />
        </Box>
      )}
    </Box>
  )
})

export interface LinkagesProps {
  type: string
  linkTargets: string[]
  drilldowns: IDropdownItem[]
  updateDrilldown: (drilldownOption: IDropdownItem, targetId: string) => void
  currentChart: IReportBlueprintChart
}
const Linkages = ({
  type,
  updateDrilldown,
  currentChart,
  linkTargets,
  drilldowns,
}) => {
  /**
   *
   * @param drilldownOption - id: drilldown-id, label: 'Chart Title', value: 'drilldown-id'
   * @param targetId - Chart | row's name | column's name
   */
  const handleDrilldownChange = (drilldownOption, targetId) => {
    updateDrilldown(drilldownOption, targetId)
  }

  const getDropdownOptionSelected = useMemo(
    () => target => {
      return drilldowns.find(
        (item: IDropdownItem) =>
          currentChart.drillDownInfo.linkages[target]?.chartName === item.label,
      )
    },
    [currentChart, drilldowns],
  )

  return (
    <Box
      w="100%"
      m="16px 0"
      border="1px solid"
      padding="16px"
      borderRadius="5px"
      borderColor="brand.gray">
      <Box w="100%">
        <Text fontWeight="bold">{type}</Text>
      </Box>
      {linkTargets.map((target, index) => (
        <Flex
          key={target}
          w="100%"
          m="8px 0">
          <Flex
            w="30%"
            alignItems="center">
            <Text
              fontWeight="bolder"
              fontSize="14px">
              {target}
            </Text>
          </Flex>
          <Flex flex={1}>
            <Dropdown
              key={index}
              items={drilldowns}
              selected={getDropdownOptionSelected(target)}
              onChange={value => handleDrilldownChange(value, target)}
              buttonProps={{
                fontSize: '14px',
                textAlign: 'left',
                w: '100%',
                borderBottom: '1px solid',
                borderBottomColor: 'gray.200',
                margin: 0,
                borderRadius: 0,
              }}
              shouldUsePortal={false}
            />
          </Flex>
        </Flex>
      ))}
    </Box>
  )
}
export default DrilldownSelector
